/**
 * Centralized Role-Based Access Control (RBAC)
 * Single source of truth for role resolution and authorization logic
 */

export const ELEVATED_ADMIN_ROLES = new Set(["admin", "super_admin", "superadmin", "owner"])
export const MANAGER_ROLE = "manager"
export const VIEWER_ROLE = "viewer"

export type PortalRole = "admin" | "manager" | "viewer"

/**
 * Resolve user's portal role from various input formats
 * Handles legacy role names and fallback logic
 * Checks multiple possible field names for role (role, userRole, roleType, etc.)
 * Also handles role as object with 'name' property
 */
export function resolvePortalRole(user: any): PortalRole {
  // If no user object, default to admin (safest default for unauthenticated flows)
  if (!user) return "admin"

  // Try multiple ways to extract role value
  let roleValue: string = ""

  // Check if role is an object with a 'name' property (backend structure)
  if (typeof user?.role === "object" && user?.role?.name) {
    roleValue = String(user.role.name)
  }
  // Check if role is a direct string
  else if (typeof user?.role === "string") {
    roleValue = user.role
  }
  // Check alternative field names
  else if (typeof user?.userRole === "string") {
    roleValue = user.userRole
  }
  else if (typeof user?.roleType === "string") {
    roleValue = user.roleType
  }

  // Normalize to lowercase
  roleValue = roleValue.toLowerCase().trim()

  // Check for elevated admin roles (multiple formats supported)
  if (ELEVATED_ADMIN_ROLES.has(roleValue)) return "admin"

  // Check explicit manager/viewer roles
  if (roleValue === MANAGER_ROLE) return MANAGER_ROLE
  if (roleValue === VIEWER_ROLE) return VIEWER_ROLE

  // FALLBACK LOGIC: If no recognized role field
  // Check if user has assigned clients - if not, they're admin
  const hasAssignedClients = Array.isArray(user?.assignedClientIds)
    && user.assignedClientIds.length > 0

  // If user has no role AND no assigned clients → PRIMARY ADMIN
  if (!roleValue && !hasAssignedClients) {
    return "admin"
  }

  // If user has no role BUT has assigned clients → VIEWER
  if (!roleValue && hasAssignedClients) {
    return "viewer"
  }

  // If role is set but unrecognized, check assigned clients
  // If they have assigned clients, treat as viewer; otherwise, admin
  return hasAssignedClients ? "viewer" : "admin"
}

/**
 * Role permission checks
 */
export const RolePermissions = {
  // Navigation & UI visibility
  canAccessTeamSettings: (role: PortalRole) => role !== VIEWER_ROLE,
  canAccessColdEmail: (role: PortalRole) => role === "admin",
  canAccessA2P: (role: PortalRole) => role === "admin",
  canSwitchClients: (role: PortalRole, assignedCount: number) =>
    role === "admin" || assignedCount > 1,

  // Data access
  canViewAllClients: (role: PortalRole) => role === "admin",
  canViewLeads: (role: PortalRole, assignedClientCount: number) =>
    role === "admin" || assignedClientCount > 0,
  canCreateLead: (role: PortalRole) => true,
  canEditLead: (role: PortalRole) => role !== "viewer", // Only viewers can't edit
  canDeleteLead: (role: PortalRole) => role === "admin",

  // Campaign/Creative access
  canCreateCampaign: (role: PortalRole) => true,
  canDeleteCampaign: (role: PortalRole) => role === "admin",

  // Audience access
  canAccessAudiences: (role: PortalRole) => true,
  canDeleteAudience: (role: PortalRole) => role === "admin",

  // Media access
  canUploadMedia: (role: PortalRole) => true,
  canDeleteMedia: (role: PortalRole) => role !== "viewer",

  // Analytics access
  canViewAnalytics: (role: PortalRole) => true,
} as const

/**
 * Get role label for display
 */
export function getRoleLabel(role: PortalRole): string {
  const labels: Record<PortalRole, string> = {
    admin: "Admin",
    manager: "Manager",
    viewer: "Viewer",
  }
  return labels[role] || role
}

/**
 * Get role variant for badge display
 */
export function getRoleBadgeVariant(
  role: PortalRole
): "default" | "secondary" | "outline" {
  const variants: Record<PortalRole, "default" | "secondary" | "outline"> = {
    admin: "default",
    manager: "secondary",
    viewer: "outline",
  }
  return variants[role] || "outline"
}

/**
 * Check if user can access a specific client
 */
export function canAccessClient(
  userRole: PortalRole,
  assignedClientIds: string[],
  clientId: string
): boolean {
  if (userRole === "admin") return true
  return assignedClientIds.includes(clientId)
}

/**
 * Get filtered client IDs based on role
 */
export function getAccessibleClientIds(
  userRole: PortalRole,
  assignedClientIds: string[],
  allClientIds: string[]
): string[] {
  if (userRole === "admin") return allClientIds
  return assignedClientIds
}

/**
 * Require specific role for access
 */
export function requireRole(userRole: PortalRole, requiredRole: PortalRole): boolean {
  if (requiredRole === "admin") return userRole === "admin"
  if (requiredRole === "manager") return userRole === "admin" || userRole === "manager"
  return true
}
